// 🔧 buy_tickets.js
// ✅ Purpose: Public-facing ticket purchase page for event attendees
// ✅ Features:
// - Shows active ticket types for the current event
// - Allows logged-in users to start Stripe Checkout
// - Uses event-level Stripe connected account through Cloud Function
// - Does not mark orders paid client-side
// - Shows clear success/error messages for 20 seconds
// - Keeps checkout logic server-side for safety
// - Fully block-commented for clarity and future devs

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Link, useSearchParams } from "react-router-dom";
import { db, functions } from "../../firebase";
import { useAuth } from "../../auth_context";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import SeoHead from "../../components/seo_head";
import AddonRequired from "../../components/AddonRequired";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";

const BuyTickets = () => {

  const { user } = useAuth();
  const { eventId, eventslug, eventDoc, ready } = useEventContext();

  const ticketsEnabled = hasPurchasedAddOn(
    eventDoc,
    "tickets"
  );
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  // =====================================================
  // ⏱️ Auto-clear notices
  // =====================================================
  useEffect(() => {
    if (!notice && !error) return;

    const timer = setTimeout(() => {
      setNotice("");
      setError("");
    }, 20000);

    return () => clearTimeout(timer);
  }, [notice, error]);

  // =====================================================
    // ✅ Stripe return success message
    // =====================================================
    useEffect(() => {
    const orderId = searchParams.get("orderId");
    const sessionId = searchParams.get("session_id");

    if (orderId && sessionId) {
        setNotice(
        `✅ Ticket purchase completed. Your order number is ${orderId}. You can view it in My Orders.`
        );
    }
    }, [searchParams]);

  // =====================================================
  // 📥 Load active tickets
  // =====================================================
  useEffect(() => {
    const loadTickets = async () => {
      if (!ready || !eventId) return;

      // ✅ Do not query ticket data if the Ticket Sales add-on is not active.
      // ✅ Firestore rules also enforce this, but this keeps the UI clean.
      if (!ticketsEnabled) {
        setTickets([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // ✅ Load tickets without a composite Firestore index
        // ✅ Purpose:
        // - Avoids requiring an active + createdAt composite index
        // - Keeps this page simple while ticket volume is small
        // - Filters active tickets in React after loading
        // - Sorts newest first using createdAt when available
        const ticketsQuery = query(
        collection(db, "events", eventId, "tickets"),
        orderBy("createdAt", "desc")
        );

        const snap = await getDocs(ticketsQuery);

        const loadedTickets = snap.docs
        .map((ticketDoc) => ({
            id: ticketDoc.id,
            ...ticketDoc.data(),
        }))
        .filter((ticket) => ticket.active === true);

        setTickets(loadedTickets);

        const startingQuantities = {};

        loadedTickets.forEach((ticket) => {
          startingQuantities[ticket.id] = 1;
        });

        setQuantities(startingQuantities);
      } catch (err) {
        logger.error("❌ Failed to load public tickets:", {
          value: err,
        });

        setError("Failed to load tickets.");
      } finally {
        setLoading(false);
      }
    };

    loadTickets();
    }, [ready, eventId, ticketsEnabled]);

  // =====================================================
  // 💵 Format cents as dollars
  // =====================================================
  const formatMoney = (priceCents = 0) => {
    return `$${(Number(priceCents || 0) / 100).toFixed(2)}`;
  };

  // =====================================================
  // 🔢 Quantity change
  // =====================================================
  const handleQuantityChange = (ticketId, value) => {
    const cleanQuantity = Math.max(1, Math.min(10, Number(value || 1)));

    setQuantities((prev) => ({
      ...prev,
      [ticketId]: cleanQuantity,
    }));
  };

  // =====================================================
  // 🛒 Start checkout
  // =====================================================
  const startCheckout = async (ticket) => {
    if (!user) {
      setError("Please log in or register before buying tickets.");
      return;
    }

    if (!eventId || !ticket?.id) return;

    try {
      setCheckingOut(true);
      setNotice("");
      setError("");

      const createCheckout = httpsCallable(
        functions,
        "createTicketCheckoutSession"
      );

      const currentUrl = `${window.location.origin}/${eventslug}/buy_tickets`;

      const result = await createCheckout({
        eventId,
        ticketId: ticket.id,
        quantity: quantities[ticket.id] || 1,
        successUrl: currentUrl,
        cancelUrl: currentUrl,
      });

      const checkoutUrl = result?.data?.url;

      if (!checkoutUrl) {
        setError("Stripe did not return a checkout link.");
        return;
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      logger.error("❌ Ticket checkout failed:", {
        value: err,
      });

      setError(
        err?.message ||
          "Could not start ticket checkout. Please try again."
      );
    } finally {
      setCheckingOut(false);
    }
  };

  const ticketsSeo = {
    ...buildEventPageSeo({
      eventDoc,
      pageKey: "tickets",
      fallbackTitle: `Buy Tickets | ${eventDoc?.name || "Event"} | Let's Go Vote`,
      fallbackDescription: `Purchase tickets for ${
        eventDoc?.name || "this event"
      } online.`,
    }),
    path: `/${eventslug}/buy_tickets`,
    image:
      eventDoc?.eventLogo ||
      "https://letsgovote.app/images/social-share-default.png",
    noindex: !isEventPublic(eventDoc),
    breadcrumbs: [
      { name: "Home", path: "/" },
      {
        name: eventDoc?.name || "Event",
        path: `/${eventslug}/event`,
      },
      {
        name: "Buy Tickets",
        path: `/${eventslug}/buy_tickets`,
      },
    ],
  };

  if (
    ready &&
    eventDoc &&
    !ticketsEnabled
  ) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Ticket Sales Add-on Required"
        description="This event has not purchased the Ticket Sales add-on."
      />
    );
  }

  if (!ready || loading) {
    return <FullPageSpinner message="Loading tickets..." />;
  }

  return (
  <>
    <SeoHead {...ticketsSeo} />    
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1000px" }}>
        <div className="row mb-4">
          <div className="col">
            <h1 className="display-5">🎟️ Buy Tickets</h1>

            <p className="lead text-muted">
              {eventDoc?.name
                ? `Purchase tickets for ${eventDoc.name}.`
                : "Purchase event tickets."}
            </p>
          </div>
        </div>

        {notice && (
        <div className="alert alert-success">
            <div>{notice}</div>

            <Link
            to={`/${eventslug}/my_orders`}
            className="btn btn-sm btn-outline-success mt-3"
            >
            View My Orders
            </Link>
        </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {!user && ticketsEnabled && (
          <div className="alert alert-info">
            Please <Link to="/login">log in</Link> or{" "}
            <Link to="/register">register</Link> before buying tickets.
          </div>
        )}

        {tickets.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-muted">
              No tickets are currently available.
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {tickets.map((ticket) => {
              const quantityAvailable = Number(ticket.quantityAvailable || 0);
              const quantitySold = Number(ticket.quantitySold || 0);
              const remaining =
                quantityAvailable > 0
                  ? Math.max(0, quantityAvailable - quantitySold)
                  : null;

              const soldOut = remaining !== null && remaining <= 0;

              return (
                <div className="col-12 col-md-6" key={ticket.id}>
                  <div className="card shadow-sm h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between gap-3">
                        <div>
                          <h2 className="h4 mb-1">{ticket.title}</h2>

                          {ticket.description && (
                            <p className="text-muted mb-2">
                              {ticket.description}
                            </p>
                          )}
                        </div>

                        <div className="fw-bold fs-5">
                          {formatMoney(ticket.priceCents)}
                        </div>
                      </div>

                      {remaining !== null && (
                        <div className="small text-muted mb-3">
                          {remaining} remaining
                        </div>
                      )}

                      <div className="row g-2 align-items-end">
                        <div className="col-5">
                          <label className="form-label">Qty</label>

                          <input
                            type="number"
                            className="form-control"
                            min="1"
                            max="10"
                            value={quantities[ticket.id] || 1}
                            onChange={(e) =>
                              handleQuantityChange(ticket.id, e.target.value)
                            }
                            disabled={checkingOut || soldOut}
                          />
                        </div>

                        <div className="col-7">
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            disabled={
                              checkingOut ||
                              soldOut ||
                              !ticketsEnabled ||
                              !user
                            }
                            onClick={() => startCheckout(ticket)}
                          >
                            {soldOut ? "Sold Out" : "Buy Ticket"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  </>
  );
};

export default BuyTickets;
